import type { Key, ReactNode } from 'react';
import { InfoIcon } from '@phosphor-icons/react';
import {
  Tab as AriaTab,
  TabList as AriaTabList,
  Tabs as AriaTabs,
} from 'react-aria-components';
import { buttonStyles } from './Button';
import { cn } from './lib/cn';
import { formatCount } from './lib/format-count';
import { Tooltip } from './Tooltip';

export type TabItem = {
  id: string;
  label: string;
  /** Rendered as a capsule inside the pill. */
  count?: number;
  /** Set when the tab is a place: it then renders as a link. */
  href?: string;
  /** Shown behind an info mark next to the label. */
  hint?: ReactNode;
};

export type TabPillsProps = {
  items: TabItem[];
  selectedKey: string;
  onSelectionChange?: (key: string) => void;
  /** What this set of tabs switches between, for screen readers. */
  label: string;
  maxCount?: number;
  className?: string;
};

export type SegmentedTabsProps = Omit<TabPillsProps, 'items'> & {
  items: Omit<TabItem, 'count'>[];
};

/**
 * A segmented control: one track, the selection a lozenge inside it. Use it for
 * two or three lasting alternatives, and `TabPills` for a list that may grow.
 */
export function SegmentedTabs({
  items,
  selectedKey,
  onSelectionChange,
  label,
  className,
}: SegmentedTabsProps) {
  return (
    <AriaTabs
      selectedKey={selectedKey}
      onSelectionChange={(key: Key) => onSelectionChange?.(String(key))}
    >
      <AriaTabList
        aria-label={label}
        items={items}
        className={cn(
          'sk:inline-flex sk:max-w-full sk:items-center sk:gap-0.5 sk:overflow-x-auto sk:rounded-full sk:p-0.5',
          'sk:bg-fill-secondary',
          className,
        )}
      >
        {item => (
          <AriaTab
            id={item.id}
            href={item.href}
            className={cn(
              'sk:inline-flex sk:h-6 sk:cursor-pointer sk:items-center sk:justify-center',
              'sk:rounded-full sk:px-3 sk:text-dense sk:font-medium sk:tracking-dense sk:whitespace-nowrap',
              'sk:text-fg-secondary sk:no-underline',
              'sk:transition-[background-color,box-shadow,color] sk:duration-100',
              'sk:hover:text-fg',
              'sk:rac-selected:inset-shadow-hairline sk:rac-selected:bg-surface sk:rac-selected:text-fg sk:rac-selected:shadow-card',
              'sk:outline-none sk:rac-focus-visible:outline-2 sk:rac-focus-visible:outline-solid sk:rac-focus-visible:outline-offset-1 sk:rac-focus-visible:outline-primary',
            )}
          >
            {item.label}
          </AriaTab>
        )}
      </AriaTabList>
    </AriaTabs>
  );
}

/** A row of filter pills, one selected. */
export function TabPills({
  items,
  selectedKey,
  onSelectionChange,
  label,
  maxCount,
  className,
}: TabPillsProps) {
  return (
    <AriaTabs
      selectedKey={selectedKey}
      onSelectionChange={(key: Key) => onSelectionChange?.(String(key))}
    >
      <AriaTabList
        aria-label={label}
        items={items}
        className={cn(
          'sk:flex sk:flex-wrap sk:items-center sk:gap-2',
          className,
        )}
      >
        {item => (
          <AriaTab
            id={item.id}
            href={item.href}
            className={buttonStyles({
              className: cn(
                'sk:text-dense sk:no-underline',
                item.count === undefined ? '' : 'sk:pr-1.5',
              ),
            })}
          >
            {({ isSelected }) => (
              <>
                {item.label}
                {item.hint ? (
                  <Tooltip content={item.hint} placement="bottom start">
                    <InfoIcon
                      size={12}
                      className="sk:shrink-0 sk:text-fg-tertiary"
                    />
                  </Tooltip>
                ) : null}
                {item.count === undefined ? null : (
                  <span
                    className={cn(
                      'sk:inline-flex sk:h-4.5 sk:min-w-4.5 sk:items-center sk:justify-center',
                      'sk:rounded-full sk:px-1.5 sk:text-xs sk:font-semibold sk:tabular-nums',
                      /* Inverted against the pill it sits in. */
                      isSelected
                        ? 'sk:bg-surface sk:text-fg'
                        : 'sk:bg-fill-secondary sk:text-fg-secondary',
                    )}
                  >
                    {formatCount(item.count, maxCount)}
                  </span>
                )}
              </>
            )}
          </AriaTab>
        )}
      </AriaTabList>
    </AriaTabs>
  );
}
